import { AUTO_DOM_ORDER_DATE } from '@wix/a11y-rules/';
import { Box, Button, Loader, TextButton } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/react';
import { useAtom, useAtomValue } from 'jotai';
import React, { useCallback, useState } from 'react';
import { isReorganizingDomOrderAtom, multiLingualDataAtom } from '../../../atoms/session-atoms';
import { BiCtaNames, BiModalTypes, BiScreenTypes } from '../../../bi/types';
import { useA11yWizardAdapter } from '../../../hooks/use-a11y-wizard-adapter';
import { useAnalyzeFullSiteAxeClick } from '../../../hooks/use-analyze-full-site-axe-click';
import { useBiLogger } from '../../../hooks/use-bi-logger';
import { useOnMount } from '../../../hooks/use-on-mount';
import { useOpenArticleByEditorHost } from '../../../hooks/use-open-article-by-editor-host';
import { useAnalyzeFullSiteQuery } from '../../../queries/use-analyze-full-site-query';
import { useReorganizePagesDOMOrder } from '../../../rule-mappers/site-level-rules/dom-order/use-reorganize-pages-dom-order';
import { dataHooks } from '../../../utils/datahooks';
import { isEditor3Host, isStudioHost } from '../../../utils/get-editor-host';
import DomOrderCheckbox from '../dom-order-checkbox';
import { useDomOrderRecoveryModal } from '../dom-order-recovery-modal';
const OLD_SITE_CREATION_DATE = '2020-1-1';
export const WizardFooterWelcome = _ref => {
  var _window$editorModel, _multiLingualData$cur, _multiLingualData$cur2;
  let {
    wasDomOrderCompleted,
    hasSavePermission,
    isPublishedBranch
  } = _ref;
  const {
    t
  } = useTranslation();
  const biLogger = useBiLogger();
  const {
    isFetching
  } = useAnalyzeFullSiteQuery();
  const {
    reorganizePagesDOMOrder
  } = useReorganizePagesDOMOrder();
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const {
    handleAnalyzeFullSiteAxeClick
  } = useAnalyzeFullSiteAxeClick();
  const {
    showDomOrderRecoveryModal
  } = useDomOrderRecoveryModal();
  const siteCreationDate = new Date(((_window$editorModel = window.editorModel) == null || (_window$editorModel = _window$editorModel.siteHeader) == null ? void 0 : _window$editorModel.dateCreated) ?? OLD_SITE_CREATION_DATE);
  const siteCreatedAfterAutoDom = siteCreationDate > AUTO_DOM_ORDER_DATE;
  const autoDOMOrderEnabled = a11yWizardAdapter.getIsSiteAutoDOMOrderEnabled();
  const multiLingualData = useAtomValue(multiLingualDataAtom);
  const [isReorganizingDomOrder, setIsReorganizingDomOrder] = useAtom(isReorganizingDomOrderAtom);
  const isStudio = isStudioHost(editorHost);
  if (isFetching && isReorganizingDomOrder) {
    setIsReorganizingDomOrder(false);
  }
  useOnMount(async () => {
    if (!isStudio) {
      return;
    }
    const isDomOrderInProgress = await a11yWizardAdapter.getIsDomReorderInProgress();
    if (isDomOrderInProgress) {
      await showDomOrderRecoveryModal();
    }
  });
  const showDomOrderCheckbox = isStudio && !siteCreatedAfterAutoDom && !wasDomOrderCompleted && autoDOMOrderEnabled && (multiLingualData == null || (_multiLingualData$cur = multiLingualData.currentLanguage) == null ? void 0 : _multiLingualData$cur.primary) !== false;
  const [isChecked, setIsChecked] = useState(true);
  const handleCancel = useCallback(() => {
    biLogger.modalCtaClick({
      ctaName: BiCtaNames.Cancel,
      modalType: BiModalTypes.UnableToOrganizeDOMOrder
    });
  }, [biLogger]);
  const handleTryAgain = useCallback(async fixOption => {
    biLogger.modalCtaClick({
      ctaName: BiCtaNames.TryAgain,
      modalType: BiModalTypes.UnableToOrganizeDOMOrder
    });
    if (fixOption) {
      await reorganizePagesDOMOrder({
        fixOption,
        handleCancel,
        handleTryAgain,
        isManuallyCompleted: false,
        screenType: BiScreenTypes.WelcomeScreen
      });
    }
  }, [biLogger, reorganizePagesDOMOrder, handleCancel]);
  const handleScanFullAxeSite = () => {
    return handleAnalyzeFullSiteAxeClick(isChecked, showDomOrderCheckbox);
  };
  return /*#__PURE__*/React.createElement(Box, {
    align: "center",
    direction: "vertical",
    padding: "SP2 0 SP4 0",
    width: "252px"
  }, showDomOrderCheckbox && !isReorganizingDomOrder && /*#__PURE__*/React.createElement(DomOrderCheckbox, {
    isChecked: isChecked,
    setIsChecked: () => setIsChecked(!isChecked)
  }), /*#__PURE__*/React.createElement(Button, {
    dataHook: dataHooks.wizardFooterWelcomeScanButton,
    disabled: (multiLingualData == null || (_multiLingualData$cur2 = multiLingualData.currentLanguage) == null ? void 0 : _multiLingualData$cur2.primary) === false || isReorganizingDomOrder || isFetching || !hasSavePermission || !isPublishedBranch,
    fullWidth: isEditor3Host(editorHost) ? false : true,
    priority: isFetching || isReorganizingDomOrder ? 'secondary' : 'primary',
    suffixIcon: isFetching || isReorganizingDomOrder ? /*#__PURE__*/React.createElement(Box, {
      margin: "-2px 0 0 4px",
      verticalAlign: "middle"
    }, /*#__PURE__*/React.createElement(Loader, {
      size: "tiny"
    })) : undefined,
    onClick: handleScanFullAxeSite
  }, t(isFetching || isReorganizingDomOrder ? 'accessibility-wizard.footer.results.scanning' : 'accessibility-wizard.welcome-screen.scan-button.cta')), /*#__PURE__*/React.createElement(Box, {
    align: "center",
    marginTop: "SP2",
    width: "252px"
  }, /*#__PURE__*/React.createElement(TextButton, {
    size: "small",
    underline: "always",
    weight: "normal",
    ellipsis: true,
    onClick: () => {
      biLogger.ctaClick({
        ctaName: BiCtaNames.LearnAboutAccessibility
      });
      openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-about-accessibility-help');
    }
  }, t('accessibility-wizard.welcome-screen.learn-more.cta'))));
};
//# sourceMappingURL=wizard-footer-welcome.js.map